// Form rules of "Nueva gestión" of Gestiones IPC (design Prompts 2, 3 and 6; 04-diseno.md §4 and §6). The option
// lists are the codes the server validates (IpcGestionConstants), not business data: everything about the comercio
// comes from the client search and the contexto endpoint.
import {
  CANAL_LABELS,
  CATEGORIAS,
  ESTADO_LABELS,
  MOTIVO_CIERRE_LABELS,
  MOTIVO_LABELS,
  MOTIVOS,
  TIPO_LABELS,
  daysBetween,
  motivoLabel,
  toOptions,
} from "../ipcFormat";
import { PRIORIDAD_LABELS, codeOf, formatDate, formatRelativeDays, labelFor, todayIso, toIsoDate } from "../../crmpostventa-shared/crmFormat";
import {
  buildClienteResumen,
  buildComercioOptions,
  terminalesDelComercio,
} from "../../crmpostventa-rollo/nuevaGestion/nuevaGestionModel";

export const OBSERVACION_MAX = 2000;
// Visita comercial preloads the next contact this many days ahead (D13, RN6).
export const DIAS_PROXIMO_CONTACTO_VISITA = 3;

export const TIPO_OPTIONS = toOptions(TIPO_LABELS, Object.keys(TIPO_LABELS));
export const CANAL_OPTIONS = toOptions(CANAL_LABELS, Object.keys(CANAL_LABELS));
export const MOTIVO_PROACTIVA_OPTIONS = toOptions(MOTIVO_LABELS, MOTIVOS.PROACTIVA);
// A gestión is born Pendiente (default), En gestión or already Resuelta (D11).
export const ESTADO_INICIAL_OPTIONS = toOptions(ESTADO_LABELS, ["PENDIENTE", "EN_GESTION", "RESUELTA"]);
export const MOTIVO_CIERRE_OPTIONS = toOptions(MOTIVO_CIERRE_LABELS, ["RESUELTO", "CLIENTE_CONTACTADO", "RESUELTO_POR_OTRA_AREA"]);
export const PRIORIDAD_OPTIONS = toOptions(PRIORIDAD_LABELS, Object.keys(PRIORIDAD_LABELS));

export const motivoOptions = (tipo) => toOptions(MOTIVO_LABELS, MOTIVOS[codeOf(tipo)] || []);

// Motives that need an observation (RN2), by IPC type or by the Proactiva category.
const OBSERVACION_OBLIGATORIA = {
  MODIFICACION: ["OTROS"],
  CONSULTA: ["CONSULTAS_ESPECIFICAS"],
  PROACTIVA: ["OTRO"],
};

export const isProactivaCategoria = (categoria) => codeOf(categoria) === CATEGORIAS.PROACTIVA;

export const observacionObligatoria = (categoria, form) => {
  const key = isProactivaCategoria(categoria) ? CATEGORIAS.PROACTIVA : codeOf(form?.tipo);
  return Boolean(form?.motivo) && (OBSERVACION_OBLIGATORIA[key] || []).includes(form.motivo);
};

// Priority the server calculates at creation (D16, D17): Reclamo Alta; Modificación, Consulta and Proactiva Media.
// Null while an IPC gestión has no type yet.
export const prioridadCalculada = (categoria, tipo) => {
  if (isProactivaCategoria(categoria)) return "MEDIA";
  if (!tipo) return null;
  return codeOf(tipo) === "RECLAMO" ? "ALTA" : "MEDIA";
};

// Form keys in the order they appear on screen, with the id suffix of their control: the first invalid one gets the
// focus. Radio groups point at their first radio.
export const FIELD_IDS = {
  tipo: "tipo",
  motivo: "motivo",
  terminal: "terminal",
  canal: "canal",
  fechaContacto: "fecha-contacto",
  proximoContacto: "proximo-contacto",
  estado: "estado",
  motivoCierre: "motivo-cierre",
  observacion: "observacion",
  prioridad: "prioridad",
};

export const fieldId = (key) => `crm-ipc-nueva-${FIELD_IDS[key]}`;
export const errorId = (key) => `${fieldId(key)}-error`;
export const firstErrorKey = (errors) => Object.keys(FIELD_IDS).find((key) => errors[key]) || null;

// ---- Dates. The inputs hold local "YYYY-MM-DDTHH:MM" (datetime-local); the POST sends ISO UTC, which the server
// converts to its local time (RN7).
const pad = (value) => String(value).padStart(2, "0");
const INPUT_DATE_TIME = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;

export const toInputDateTime = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;

export const parseInputDateTime = (value) => {
  const match = INPUT_DATE_TIME.exec(String(value ?? ""));
  if (!match) return null;
  const [, year, month, day, hours, minutes] = match.map(Number);
  return new Date(year, month - 1, day, hours, minutes);
};

const addDays = (date, days) => {
  const next = new Date(date.getTime());
  next.setDate(next.getDate() + days);
  return next;
};

export const toServerDateTime = (value) => parseInputDateTime(value)?.toISOString() ?? null;

// "20/10/2026 14:30".
export const formatDateTime = (date) => `${formatDate(date)} ${pad(date.getHours())}:${pad(date.getMinutes())}`;

// ---- Comercio of the gestión. The client search answers one row per alta, grouped by client like the rest of CRM
// Postventa; a gestión belongs to one comercio, so step 1 shows one card per comercio of each client, pointing at an
// ALTA alta of that comercio (Rollo's buildComercioOptions).
export const buildComercioCards = (grupos) =>
  (grupos || []).flatMap((grupo) => {
    const resumen = buildClienteResumen(grupo);
    return buildComercioOptions(grupo).map((comercio) => ({
      key: `${grupo.groupKey}|${comercio.key}`,
      clienteId: comercio.clienteId,
      nombre: resumen.nombre,
      razonSocial: resumen.razonSocial,
      cuit: resumen.cuit,
      numeroComercio: comercio.numero,
      terminales: comercio.terminales,
    }));
  });

const ACTIVO_KEY_PREFIX = "activo-";
export const activoKey = (clienteId) => `${ACTIVO_KEY_PREFIX}${clienteId}`;
// Whether a card key is the one of the CRM active comercio (see activoKey).
export const isActivoKey = (key) => typeof key === "string" && key.startsWith(ACTIVO_KEY_PREFIX);

// Card of the CRM active comercio (Prompt 7, point 4), from its contexto, which step 2 then reuses.
export const buildActivoCard = (contexto) => {
  const cliente = contexto?.cliente;
  if (!cliente?.id) return null;
  return {
    key: activoKey(cliente.id),
    clienteId: cliente.id,
    nombre: cliente.nombre,
    razonSocial: cliente.razonSocial,
    cuit: cliente.cuit,
    numeroComercio: cliente.numeroComercio,
    terminales: terminalesDelComercio(contexto.terminales, cliente.numeroComercio).length,
    activo: true,
    contexto,
  };
};

// The contexto lists every terminal of the CUIT: the gestión only offers the chosen comercio's ones.
export const terminalesDeContexto = (contexto) => terminalesDelComercio(contexto?.terminales, contexto?.cliente?.numeroComercio);

// ---- Form.
// The terminal: the one preloaded from the Ficha when it belongs to the comercio; in IPC also the only terminal of
// the comercio. A Proactiva only keeps the preloaded one (it has no terminal field).
export const buildInitialForm = ({ categoria, tipo = null, terminales = [], terminalPrecargada = null, now = new Date() }) => {
  const numeros = terminales.map((terminal) => String(terminal.numeroTerminal));
  const precargada = terminalPrecargada && numeros.includes(String(terminalPrecargada)) ? String(terminalPrecargada) : "";
  const proactiva = isProactivaCategoria(categoria);
  const fechaContacto = toInputDateTime(now);
  return {
    tipo: proactiva ? "" : codeOf(tipo),
    motivo: "",
    terminal: precargada || (!proactiva && numeros.length === 1 ? numeros[0] : ""),
    canal: "",
    mostrarCanal: false,
    fechaContacto,
    fechaContactoInicial: fechaContacto,
    estado: "PENDIENTE",
    motivoCierre: "",
    proximoContacto: "",
    proximoAutomatico: false,
    observacion: "",
    prioridad: "",
  };
};

// A new IPC type clears its motive and the priority fixed by hand (it was chosen for the previous type).
export const changeTipo = (form, tipo) => ({ ...form, tipo, motivo: "", prioridad: "" });

// Proactiva: Visita comercial preloads the next contact 3 days ahead unless the asesor wrote one; Otro clears the
// preloaded one. The asesor can change or clear it.
export const changeMotivoProactiva = (form, motivo, now = new Date()) => {
  if (motivo === "VISITA_COMERCIAL" && (!form.proximoContacto || form.proximoAutomatico)) {
    return { ...form, motivo, proximoContacto: toInputDateTime(addDays(now, DIAS_PROXIMO_CONTACTO_VISITA)), proximoAutomatico: true };
  }
  if (motivo !== "VISITA_COMERCIAL" && form.proximoAutomatico) {
    return { ...form, motivo, proximoContacto: "", proximoAutomatico: false };
  }
  return { ...form, motivo };
};

// Validation on submit, with each message as an instruction (Prompt 6).
export const validateGestionForm = (form, categoria, now = new Date()) => {
  const errors = {};
  const proactiva = isProactivaCategoria(categoria);

  if (proactiva) {
    if (!form.motivo) errors.motivo = "Elegí el motivo o la acción";
  } else {
    if (!form.tipo) errors.tipo = "Elegí el tipo de gestión";
    else if (!form.motivo) errors.motivo = "Elegí el motivo";
    if (!form.canal) errors.canal = "Elegí el canal de contacto";
  }

  const contacto = parseInputDateTime(form.fechaContacto);
  if (!contacto) errors.fechaContacto = proactiva ? "Ingresá la fecha y hora" : "Ingresá la fecha y hora del contacto";
  else if (contacto > now) errors.fechaContacto = "Elegí una fecha anterior a ahora";

  const resuelta = form.estado === "RESUELTA";
  if (!resuelta && String(form.proximoContacto ?? "").trim()) {
    const proximo = parseInputDateTime(form.proximoContacto);
    if (!proximo || proximo <= now) errors.proximoContacto = "Elegí una fecha posterior a ahora";
  }
  if (resuelta && !form.motivoCierre) errors.motivoCierre = "Elegí el motivo de cierre";

  const observacion = String(form.observacion ?? "").trim();
  if (!observacion && observacionObligatoria(categoria, form)) {
    errors.observacion = proactiva ? "Contá qué acción vas a hacer" : "Contá el detalle: es obligatorio para este motivo";
  } else if (observacion.length > OBSERVACION_MAX) {
    errors.observacion = `Acortá la observación a ${OBSERVACION_MAX} caracteres como máximo`;
  }

  return errors;
};

const textOrNull = (value) => String(value ?? "").trim() || null;

// Body of POST gestiones without the correlationId (the caller adds it). An untouched contact date goes as null, so
// the server stores the moment of the alta (RN7) and a browser clock ahead of the server never makes it "future".
export const buildCrearPayload = ({ clienteId, categoria, form }) => {
  const proactiva = isProactivaCategoria(categoria);
  const resuelta = form.estado === "RESUELTA";
  return {
    clienteId,
    numeroTerminal: form.terminal || null,
    categoria: proactiva ? CATEGORIAS.PROACTIVA : CATEGORIAS.IPC,
    tipo: proactiva ? null : form.tipo,
    motivoCodigo: form.motivo,
    canal: proactiva ? (form.mostrarCanal && form.canal) || null : form.canal,
    fechaContacto: form.fechaContacto === form.fechaContactoInicial ? null : toServerDateTime(form.fechaContacto),
    estado: form.estado,
    motivoCierre: resuelta ? form.motivoCierre : null,
    fechaProximoContacto: resuelta ? null : toServerDateTime(form.proximoContacto),
    prioridad: form.prioridad || null,
    observacion: textOrNull(form.observacion),
  };
};

// ---- Similar open gestión (CU9, S4): same type and motive in IPC, same motive in Proactiva. It never blocks.
export const findSimilar = (abiertas, categoria, form) => {
  const proactiva = isProactivaCategoria(categoria);
  if (!form?.motivo || (!proactiva && !form.tipo)) return null;
  return (
    (abiertas || []).find(
      (gestion) =>
        !gestion.esHistorico &&
        gestion.activo !== false &&
        isProactivaCategoria(gestion.categoria) === proactiva &&
        (proactiva || codeOf(gestion.tipo) === form.tipo) &&
        codeOf(gestion.motivoCodigo) === form.motivo
    ) || null
  );
};

const ARTICULOS = {
  MODIFICACION: "una modificación abierta",
  RECLAMO: "un reclamo abierto",
  CONSULTA: "una consulta abierta",
};

const articuloDe = (gestion) =>
  isProactivaCategoria(gestion.categoria) ? "una proactiva abierta" : ARTICULOS[codeOf(gestion.tipo)] || "una gestión abierta";

// "Este comercio ya tiene un reclamo abierto por Liquidación, Pendiente desde hace 2 días."
export const similarTitle = (gestion, today = todayIso()) => {
  const dias = daysBetween(toIsoDate(gestion.fechaAlta), today);
  const desde = dias === null ? "" : ` desde ${formatRelativeDays(dias)}`;
  return `Este comercio ya tiene ${articuloDe(gestion)} por ${motivoLabel(gestion)}, ${labelFor(ESTADO_LABELS, gestion.estado)}${desde}.`;
};

export const similarAckText = (gestion) => `Se va a crear igual, aunque ya hay ${articuloDe(gestion)} por ${motivoLabel(gestion)}.`;

// ---- Results.
export const buildToast = (cliente, payload) => ({
  title:
    payload.estado === "RESUELTA"
      ? "Gestión registrada como resuelta"
      : isProactivaCategoria(payload.categoria)
        ? "Gestión proactiva creada"
        : "Gestión creada",
  text: [
    cliente?.nombre || cliente?.razonSocial || "",
    isProactivaCategoria(payload.categoria) ? "Proactiva" : TIPO_LABELS[payload.tipo] || "",
    MOTIVO_LABELS[payload.motivoCodigo] || payload.motivoCodigo,
  ]
    .filter(Boolean)
    .join(" · "),
});

export const describeCreateError = (error) => ({
  title: "No pudimos guardar la gestión.",
  text: error?.status ? error.message || "Probá de nuevo en unos segundos." : "Revisá la conexión y probá de nuevo. Lo que cargaste se mantiene.",
});
